import { EditIcon, LocationIcon } from '../../components/ui/icons';
import { surfaceShadows } from '../../surfaceStyles';
import { recordKey } from '../collection/multiCollectionIdentity';
import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { CardSize, DISPLAY_SIZE_PROFILES } from '../../hooks/useUiPreferences';
import {
  CollectionSelectionController,
  CollectionView,
} from '../collection/types';
import { useCollectionEntryInteraction } from '../collection/hooks/useCollectionEntryInteraction';
import { userProfileResolverService } from '../../services/userProfileResolverService';
import { formatDate } from '../../utils/dateFormat';

export interface LocationRecord {
  id: string;
  name: string;
  collectionName?: string;
  description: string;
  createdAt?: string;
  lastEditedAt?: string;
  lastEditedBy?: string;
}

export function LocationEntry({
  location,
  itemCount,
  view,
  cardSize,
  selection,
  visibleIds,
  onOpen,
  currentUserId,
  currentUsername,
  collectionId,
  membershipVersion,
}: {
  location: LocationRecord;
  itemCount?: number;
  view: CollectionView;
  cardSize: CardSize;
  selection: CollectionSelectionController;
  visibleIds?: string[];
  onOpen: () => void;
  currentUserId?: string;
  currentUsername?: string;
  collectionId?: string;
  membershipVersion?: number;
}) {
  const size = DISPLAY_SIZE_PROFILES[cardSize];
  const [editorUsername, setEditorUsername] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const editorId = location.lastEditedBy;
    if (!editorId) {
      setEditorUsername(null);
      return;
    }
    if (currentUserId && editorId === currentUserId && currentUsername) {
      setEditorUsername(currentUsername);
      return;
    }
    if (!collectionId || membershipVersion === undefined) {
      setEditorUsername(null);
      return;
    }
    setEditorUsername(null);
    void userProfileResolverService
      .resolveUsername(collectionId, editorId, membershipVersion)
      .then((resolved) => {
        if (active) setEditorUsername(resolved);
      });
    return () => {
      active = false;
    };
  }, [
    location.lastEditedBy,
    currentUserId,
    currentUsername,
    collectionId,
    membershipVersion,
  ]);
  const interaction = useCollectionEntryInteraction({
    id: recordKey(location),
    label: location.name,
    selection,
    onOpen,
    visibleIds,
  });
  const selected = interaction.selected;
  const checkbox = selection.active ? (
    <Checkbox
      checked={selected}
      inputProps={{
        'aria-label': `${selected ? 'Deselect' : 'Select'} ${location.name}`,
      }}
      onClick={(event) => {
        event.stopPropagation();
        interaction.toggle(event);
      }}
      sx={{ flexShrink: 0, order: view === 'list' ? 1 : undefined }}
    />
  ) : null;
  const status =
    itemCount === undefined ? null : (
      <Chip
        label={
          itemCount ? `${itemCount} item${itemCount === 1 ? '' : 's'}` : 'Empty'
        }
        size="small"
        color="default"
        variant="outlined"
      />
    );

  const editedChip =
    location.lastEditedAt || location.lastEditedBy ? (
      <Chip
        icon={<EditIcon fontSize="small" />}
        label={
          location.lastEditedAt
            ? `Edited ${formatDate(location.lastEditedAt)}${editorUsername ? ` by @${editorUsername}` : ''}`
            : editorUsername
              ? `Edited by @${editorUsername}`
              : 'Edited'
        }
        size="small"
        variant="outlined"
        sx={{
          fontSize: '0.75rem',
          color: 'text.secondary',
          border: 0,
          boxShadow: 'none',
          bgcolor: 'transparent',
          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 },
          height: 'auto',
        }}
        data-testid={`location-edited-${location.id}`}
      />
    ) : null;

  if (view === 'grid') {
    return (
      <Card
        data-testid={`location-card-${location.id}`}
        sx={{
          minWidth: 0,
          height: '100%',
          position: 'relative',
          bgcolor: selected ? 'action.selected' : 'background.paper',
          border: 0,
          boxShadow: selected
            ? 'inset 0 0 0 1px currentColor'
            : surfaceShadows.raised,
          color: selected ? 'primary.main' : 'text.primary',
          borderRadius: 1,
          transform: interaction.pressing ? 'scale(.98)' : 'none',
          transition: 'transform 120ms',
        }}
      >
        <CardActionArea
          {...interaction.actionProps}
          sx={{ height: '100%', alignItems: 'stretch', touchAction: 'pan-y' }}
        >
          <CardContent
            data-testid={`location-card-content-${location.id}`}
            sx={{
              minHeight: size.locationGridMinHeight,
              p: size.locationGridPadding,
              '&:last-child': { pb: size.locationGridPadding },
              display: 'flex',
              flexDirection: 'column',
              gap: size.locationGridContentGap,
            }}
          >
            <LocationIcon sx={{ color: 'primary.main', fontSize: 24 }} />
            <Typography
              color="text.primary"
              fontWeight={650}
              sx={{ overflowWrap: 'anywhere', pr: selection.active ? 4 : 0 }}
            >
              {location.name}
            </Typography>
            {location.collectionName && (
              <Typography variant="caption" color="text.secondary">
                {location.collectionName}
              </Typography>
            )}
            <Typography
              color={location.description ? 'text.secondary' : 'text.disabled'}
              sx={{
                flex: 1,
                display: '-webkit-box',
                WebkitLineClamp: size.locationGridDescriptionLines,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                overflowWrap: 'anywhere',
              }}
            >
              {location.description || 'No description'}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              alignItems="center"
              useFlexGap
            >
              {status}
              {editedChip}
            </Stack>
          </CardContent>
        </CardActionArea>
        {selection.active && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              width: 44,
              height: 44,
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {checkbox}
          </Box>
        )}
      </Card>
    );
  }

  return (
    <ListItem
      disablePadding
      sx={{
        minHeight: size.itemListRowHeight,
        bgcolor: selected ? 'action.selected' : 'background.paper',
        border: 0,
        borderRadius: 1,
        boxShadow: surfaceShadows.control,
        overflow: 'hidden',
      }}
    >
      <ListItemButton
        {...interaction.actionProps}
        sx={{
          minHeight: size.itemListRowHeight,
          minWidth: 0,
          py: size.locationListPaddingY,
          touchAction: 'pan-y',
        }}
      >
        <ListItemText
          disableTypography
          sx={{ minWidth: 0, my: 0 }}
          primary={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography
                component="span"
                fontWeight={selected ? 700 : 600}
                sx={{ overflowWrap: 'anywhere' }}
              >
                {location.name}
              </Typography>
              {status}
              {editedChip}
            </Box>
          }
          secondary={
            <>
              {location.collectionName && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  {location.collectionName}
                </Typography>
              )}
              <Typography
                component="span"
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: location.description
                    ? 'text.secondary'
                    : 'text.disabled',
                  display: '-webkit-box',
                  WebkitLineClamp: size.locationListDescriptionLines,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  overflowWrap: 'anywhere',
                }}
              >
                {location.description || 'No description'}
              </Typography>
            </>
          }
        />
      </ListItemButton>
      {checkbox}
    </ListItem>
  );
}
